<template>
  <div class="gs-overlay" @click.self="close" v-show="!isTeacherPacedChecking">
    <div class="gs-game-card" v-show="!isResuming && !showTeamSelection && !showGameTracking && !isTeacherPacedAutoStarting">
      <!-- HEADER -->
      <div class="gs-header">
        <div class="gs-left">
          <div class="gs-icon-box">
            <i class="fa fa-gamepad"></i>
          </div>
          <div class="gs-header-text">
            <div class="gs-title">START GAME</div>
            <div class="gs-subtitle">CONFIGURE GAME MODE</div>
          </div>
        </div>
        <div class="gs-close" @click="close"><i class="fa fa-times"></i></div>
      </div>

      <!-- SELECT SLIDES -->
      <div class="gs-section">
        <div class="gs-section-title">
          <span class="gs-bar gs-bar-cyan"></span> SELECT SLIDES
          <span class="gs-badge">{{ selectedSlides.length }} SELECTED</span>
        </div>
        <div class="gs-slides-list">
          <div v-for="slide in slides" :key="slide.id" class="gs-slide-card" :class="{active: isSelected(slide.id)}" @click="toggleSlide(slide.id)">
            <div class="gs-left-part">
              <div class="gs-custom-checkbox" :class="{checked: isSelected(slide.id)}">
                <i v-if="isSelected(slide.id)" class="fa fa-check"></i>
              </div>
              <div class="gs-thumb" :style="slide.pic ? `background-image: url(${Fn.hashToUrl(slide.pic)})` : ''" @click.stop="previewImage(slide.pic)"></div>
              <div class="gs-content">
                <div class="gs-name" style="background: transparent">{{ slide.title }}</div>
                <div class="gs-meta">
                  <span v-if="slide.level" class="gs-gray-text">BLOOM LEVEL {{ slide.level }}</span>
                  <span v-if="slide.type" class="gs-white-bold">{{ slide.type }}</span>
                </div>
              </div>
            </div>

            <div class="gs-right-part">
              <div v-if="slide.cpa" class="gs-view" @click.stop="previewCpa(slide)"><i class="fa fa-eye"></i> VIEW</div>
              <div v-else-if="!slide.title.includes('VIDEO')" class="gs-view" @click.stop="previewImage(slide.pic)"><i class="fa fa-eye"></i> VIEW</div>
              <div v-if="slide.points" class="gs-points">{{ slide.points }} POINTS</div>
            </div>
          </div>
        </div>
      </div>

      <!-- TIMER -->
      <div class="gs-section">
        <div class="gs-section-title"><span class="gs-bar gs-bar-pink"></span> SET TIMER PER QUESTION</div>

        <div class="gs-timer">
          <div v-for="time in timers" :key="time" class="gs-timer-btn" :class="{active: selectedTime === time}" @click="selectedTime = time">{{ time }}S</div>
        </div>

        <div class="gs-warning">WARNING : SHORT TIMERS INCREASE DIFFICULTY LEVEL</div>
      </div>

      <!-- FOOTER -->
      <div class="gs-footer">
        <button class="gs-cancel" @click="close">CANCEL</button>
        <button class="gs-start" @click="handleNext" :disabled="selectedSlides.length === 0" :class="{disabled: selectedSlides.length === 0}">
          NEXT <i class="fa fa-play" style="font-size: 10px; margin-left: 4px"></i>
        </button>
      </div>
    </div>
    <!-- IMAGE PREVIEW OVERLAY -->
    <div v-if="previewPicUrl" class="gs-preview-overlay" @click.stop="closePreview">
      <div class="gs-preview-close" @click.stop="closePreview"><i class="fa fa-times"></i></div>
      <img :src="Fn.hashToUrl(previewPicUrl)" alt="Preview" class="gs-preview-image" @click.stop />
    </div>

    <!-- CPA PREVIEW OVERLAY -->
    <div v-if="previewCpaData" class="gs-preview-overlay" @click.stop="closePreview">
      <div class="gs-cpa-modal" @click.stop>
        <div class="gs-cpa-close" @click.stop="closePreview"><i class="fa fa-times"></i></div>
        <div class="gs-cpa-question" v-html="previewCpaData.abstractData"></div>
        <div class="gs-cpa-options">
          <div v-for="(opt, idx) in previewCpaData.options" :key="idx" class="gs-cpa-option">
            <i v-if="opt.on" class="fa fa-check gs-cpa-tick"></i>
            <span :class="{'gs-cpa-indent': !opt.on}">{{ opt.val }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- TEAM SELECTION MODAL -->
    <TeamSelectionModal v-if="showTeamSelection" :gameConfig="gamePayload" @close="handleTeamPreviewClose" @start="handleStartFromTeam" />

    <!-- LOADING OVERLAY FOR TEACHER PACED AUTO START -->
    <div
      class="gs-game-card"
      style="
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 300px;
        background: transparent;
        border: none;
        box-shadow: none;
      "
      v-if="isTeacherPacedAutoStarting && !showTeamSelection && !showGameTracking">
      <div style="font-size: 40px; color: #00ffff; margin-bottom: 20px"><i class="fa fa-spinner fa-spin"></i></div>
      <div style="font-size: 14px; color: #fff; font-weight: bold; letter-spacing: 2px">PREPARING GAME...</div>
    </div>

    <!-- GAME TRACKING PAGE -->
    <GameTrackingPage v-if="showGameTracking" :gameConfig="gamePayload" :teams="activeTeams" :isDashboard="isDashboardMode" @end-game="handleEndGame" />
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import TeamSelectionModal from './TeamSelectionModal.vue'
import GameTrackingPage from './GameTrackingPage.vue'

export default {
  name: 'GameSetting',
  components: {TeamSelectionModal, GameTrackingPage},
  props: {
    resumeGameId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isResuming: !!this.resumeGameId,
      isTeacherPacedChecking: false,
      isTeacherPacedAutoStarting: false,
      selectedSlideIds: [],
      selectedSlides: [],
      selectedTime: 120,
      timers: [120, 150, 180],
      previewPicUrl: null,
      previewCpaData: null,
      showTeamSelection: false,
      showGameTracking: false,
      activeTeams: [],
      gamePayload: null,
      BloomLabels: ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create'],
    }
  },
  computed: {
    ...mapGetters(['getPageById', 'sessionId', 'getPageId']),
    ...mapGetters('teacher', ['questions']),

    slides() {
      if (!this.questions || !this.session?.task?.pages) return []

      const map = new Map()

      // Step 1: Group questions by normalized pageId
      this.questions.forEach((q) => {
        const pageId = this.getNormalizedPageId(q.id.includes('::') ? q.id : q.page)

        const pageData = this.getPageById(pageId)
        if (!pageData || !pageData._id) return

        if (!map.has(pageId)) {
          map.set(pageId, {
            id: pageId,
            pic: pageData.pic || '',
            questions: [],
          })
        }

        map.get(pageId).questions.push(q)
      })
      return this.session.task.pages
        .map((page) => {
          const pageId = this.getNormalizedPageId(page._id)

          if (!map.has(pageId)) return null

          const slide = map.get(pageId)
          const q = slide.questions[0]

          let titleType = q.type ? q.type.toUpperCase() : 'TEXT'
          if (q.type === 'choice') titleType = 'MULTIPLE CHOICE'
          if (q.id.includes('::')) titleType = 'VIDEO'

          return {
            id: slide.id,
            pic: slide.pic,

            title: `${titleType} QUESTION`,
            level: q.bloom?.[0] ?? null,
            type: q.bloom?.[0] != null && this.BloomLabels[q.bloom[0]] ? this.BloomLabels[q.bloom[0]].toUpperCase() : null,

            points: q.type === 'choice' && (q.options || []).some((o) => o.on === true) ? 2 : null,

            cpa: q.cpa?.outcome || null,
            abstractData: q.abstract?.data || '',
            options: q.options || [],

            questions: slide.questions,
          }
        })
        .filter(Boolean)
    },
    isTeacherPaced() {
      const status = this.$store.state.session && this.$store.state.session.status
      return status && status !== 'student' && status !== 'close'
    },
    isDashboardMode() {
      const path = (this.$route && this.$route.path) || window.location.pathname
      return path.includes('/d/')
    },
    session() {
      return this.$store.state.session
    },
  },

  watch: {
    'session.gameOnTracking': {
      handler(val, oldVal) {
        // Sync state if it changed remotely (e.g. status 1 -> 2)
        // We no longer close the modal automatically here (!val check),
        // because GameTrackingPage already handles the 'end-game' flow
        // by listening to the game-on service socket. Removing this
        // prevents premature closure during dashboard refresh.
        if (val && this.showGameTracking) {
          if (val.gameConfig) this.gamePayload = val.gameConfig
          if (val.teams) this.activeTeams = val.teams
        }
      },
    },
  },

  mounted() {
    document.body.appendChild(this.$el)
    console.log('')
    if (this.resumeGameId) {
      this.handleResumeGame(this.resumeGameId)
    } else {
      const status = this.$store.state.session && this.$store.state.session.status
      if (status === 'live') {
        this.isTeacherPacedChecking = true
        const pageId = this.getPageId
        const currentQ = this.questions && this.questions.find((q) => {
          const qPageId = q.id && q.id.includes('::') ? q.id : q.page
          return this.getNormalizedPageId(qPageId) === pageId
        })

        if (pageId && currentQ) {
          this.isTeacherPacedChecking = false
          this.isTeacherPacedAutoStarting = true
          this.selectedSlides = [pageId]
          this.selectedTime = 180
          this.handleNext()
        } else {
          this.isTeacherPacedChecking = false
          if (this.$message) {
            this.$message.warning('There is no question in this slide')
          } else {
            alert('There is no question in this slide')
          }
          this.close()
        }
      }
    }
  },
  destroyed() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }
  },
  methods: {
    getNormalizedPageId(page) {
      if (!page) return null
      return page.includes('::') ? page.split('::')[1] : page
    },
    
    toggleSlide(id) {
      if (this.selectedSlides.includes(id)) {
        this.selectedSlides = this.selectedSlides.filter((x) => x !== id)
      } else {
        this.selectedSlides.push(id)
      }
    },

    isSelected(id) {
      return this.selectedSlides.includes(id)
    },
    close() {
      this.$emit('close')
    },
    previewImage(picUrl) {
      if (picUrl) {
        this.previewPicUrl = picUrl
      }
    },
    previewCpa(slide) {
      if (slide && slide.cpa) {
        this.previewCpaData = slide
      }
    },
    closePreview() {
      this.previewPicUrl = null
      this.previewCpaData = null
    },
    handleTeamPreviewClose() {
      this.showTeamSelection = false
      if (this.isTeacherPacedAutoStarting) {
        this.$emit('close')
      }
    },
    async handleNext() {
      const fullQuestions = this.selectedSlides.flatMap((pageId) => {
        const slide = this.slides.find((s) => s.id === pageId)
        console.log('slide in full question', slide, pageId)
        if (!slide) return []

        return slide.questions.map((q) => ({
          _id: q._id,
          id: q._id || q.id,
          page: pageId,
          type: q.type,
          multi: q.multi,
          options: q.options || [],
          score: q.score || {},
          data: q.data || null,
          pic: slide.pic,
          title: slide.title,
          points: slide.points || (q.score?.points ?? 2),
        }))
      })
      const orderedSlides = this.slides.filter((slide) => this.selectedSlides.includes(slide.id))
      const slidesPayload = orderedSlides.map((slide, slideIndex) => ({
        pageId: slide.id,
        serial: slideIndex + 1,

        pic: slide.pic,
        title: slide.title,
        points: slide.points,

        questions: slide.questions.map((q, qIndex) => ({
          _id: q._id,
          id: q._id || q.id,

          slideSerial: slideIndex + 1,
          questionSerial: qIndex + 1,

          type: q.type,
          multi: q.multi,
          options: q.options || [],
          score: q.score || {},
          data: q.data || null,
        })),
      }))

      const payload = {
        slides: slidesPayload,
        slideIds: this.selectedSlides,
        timing: this.selectedTime,
        time: this.selectedTime,
      }

      try {
        if (window.App) {
          const record = await window.App.service('game-on').create({
            teamGameId: this.session.gameOn?.teamGameId,
            sessionId: this.sessionId,
            pageIds: [...new Set(fullQuestions.map((q) => q.page))],
            timePerQuestion: this.selectedTime,
          })

          payload.gameOnRecord = record
        }
      } catch (err) {
        console.error('Failed to create game-on record:', err)
      }
      console.log('handlenext', payload)
      this.gamePayload = payload
      this.showTeamSelection = true
    },

    async handleStartFromTeam(finalPayload) {
      if (finalPayload.gameOnRecord) {
        this.$set(this.gamePayload, 'gameOnRecord', finalPayload.gameOnRecord)
      }
      this.showTeamSelection = false
      this.showGameTracking = true
      this.activeTeams = finalPayload.teams

      // Sync to session for projector view
      try {
        await this.$store.dispatch('upSession', {
          gameOnTracking: {
            gameConfig: this.gamePayload,
            teams: finalPayload.teams,
          },
        })
      } catch (e) {
        console.error('Failed to sync gameOnTracking to session:', e)
      }
    },
    async handleEndGame(updatedConfig) {
      if (updatedConfig) {
        this.gamePayload = updatedConfig
      }

      // Clear from session
      try {
        await this.$store.dispatch('upSession', {gameOnTracking: null})
      } catch (e) {
        console.error('Failed to clear gameOnTracking from session:', e)
      }

      this.showGameTracking = false
      this.$emit('end-game', this.gamePayload)
      this.$emit('close')
    },
    async handleResumeGame(recordId) {
      try {
        if (!window.App) {
          this.isResuming = false
          return
        }

        const record = await window.App.service('game-on').get(recordId)

        if (!record || record.status !== 1) {
          this.isResuming = false
          return
        }

        if (!this.slides || !this.slides.length) {
          console.error('Slides not loaded. Cannot resume game.')
          this.isResuming = false
          return
        }

        const orderedSlides = (record.pageIds || []).map((pageId) => this.slides.find((s) => s.id === pageId)).filter(Boolean)

        const slidesPayload = orderedSlides.map((slide, slideIndex) => ({
          pageId: slide.id,
          serial: slideIndex + 1,

          pic: slide.pic,
          title: slide.title,
          points: slide.points,

          questions: (slide.questions || []).map((q, qIndex) => ({
            _id: q._id,
            id: q._id || q.id,

            slideSerial: slideIndex + 1,
            questionSerial: qIndex + 1,

            type: q.type,
            multi: q.multi,
            options: q.options || [],
            score: q.score || {},
            data: q.data || null,
          })),
        }))

        this.gamePayload = {
          slides: slidesPayload,
          slideIds: record.pageIds,
          timing: record.timePerQuestion || 120,
          time: record.timePerQuestion || 120,
          gameOnRecord: record,
        }

        const dbTeams = record.teams || []
        const dbMembers = record.members || []

        const classMembers = this.$store?.state?.teacher?.rooms?.members || []

        const coreColors = [
          '#FF3366',
          '#FF9933',
          '#FFCC00',
          '#33CC33',
          '#3399FF',
          '#9933FF',
          '#FF66B2',
          '#FFB266',
          '#FFE566',
          '#66E566',
          '#66B2FF',
          '#B266FF',
          '#FF0000',
          '#FF8000',
          '#FFFF00',
          '#00FF00',
          '#0000FF',
          '#8000FF',
          '#CC0000',
          '#CC6600',
          '#CCCC00',
          '#00CC00',
          '#0000CC',
          '#6600CC',
        ]

        let cIdx = 0

        this.activeTeams = dbTeams.map((t, idx) => {
          const teamColor = coreColors[idx % coreColors.length]

          const teamMembers = dbMembers
            .filter((m) => m.teamId === t.id)
            .map((m) => {
              const user = classMembers.find((c) => c._id === m.uid) || {}

              const mName = typeof m.name === 'string' ? m.name : Array.isArray(m.name) ? m.name.join(' ') : null

              const cName = user.nickname || user.name

              return {
                id: m.uid,
                name: mName || cName || 'Unknown User',
                avatar: m.avatar || user.avatar || user.pic || '',
                score: m.score || 0,
                originalMemberData: m,
                colorIndex: cIdx++,
              }
            })

          return {
            id: t.id,
            name: t.name || `TEAM ${t.id}`,
            score: t.score || 0,
            progress: t.progress || 0,
            color: teamColor,
            members: teamMembers,
          }
        })

        this.showTeamSelection = false
        this.showGameTracking = true
        this.isResuming = false

        try {
          await this.$store.dispatch('upSession', {
            gameOnTracking: {
              gameConfig: this.gamePayload,
              teams: this.activeTeams,
            },
          })
        } catch (e) {
          console.error('Failed to sync gameOnTracking during resume:', e)
        }
      } catch (err) {
        console.error('Failed to resume game-on tracking from record:', err)
        this.isResuming = false
      }
    },
  },
}
</script>

<
<style scoped>
.gs-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
}

.gs-game-card {
  width: 640px;
  background: linear-gradient(180deg, #0d1a1a, #091315);
  border: 1px solid #1a3c40;
  border-radius: 12px;
  color: #fff;
  font-family: Arial, sans-serif;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

/* HEADER */
.gs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 24px;
  border-bottom: 1px solid #1a3c40;
  background: #0d2525;
}

.gs-left {
  display: flex;
  align-items: center;
}

.gs-icon-box {
  background: transparent;
  color: #00ffff;
  font-size: 24px;
  margin-right: 12px;
}

.gs-header-text {
  display: flex;
  flex-direction: column;
}

.gs-title {
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 1px;
}

.gs-subtitle {
  font-size: 10px;
  color: #00ffff;
  letter-spacing: 0.5px;
}

.gs-close {
  cursor: pointer;
  font-size: 18px;
  color: #8c99a0;
  transition: color 0.2s;
}

.gs-close:hover {
  color: #fff;
}

/* SECTION */
.gs-section {
  padding: 24px 24px 10px 24px;
}

.gs-section-title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 16px;
  letter-spacing: 0.5px;
}

.gs-bar {
  display: inline-block;
  width: 4px;
  height: 14px;
  border-radius: 2px;
  margin-right: 8px;
}

.gs-bar-cyan {
  background-color: #00ffff;
}

.gs-bar-pink {
  background-color: #ff00ff;
}

.gs-badge {
  margin-left: auto;
  border: 1px solid #00ffff;
  color: #00ffff;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  background-color: #072e30;
}

/* SLIDES LIST */
.gs-slides-list {
  max-height: 350px;
  overflow-y: auto;
  padding-right: 6px;
  margin-right: -6px;
}

/* Custom Scrollbar for dark theme */
.gs-slides-list::-webkit-scrollbar {
  width: 6px;
}

.gs-slides-list::-webkit-scrollbar-track {
  background: #0d2729;
  border-radius: 4px;
}

.gs-slides-list::-webkit-scrollbar-thumb {
  background: #1a3c40;
  border-radius: 4px;
}

.gs-slides-list::-webkit-scrollbar-thumb:hover {
  background: #336b6b;
}

/* SLIDE CARD */
.gs-slide-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #0d2729;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.gs-slide-card:hover {
  background: #113133;
}

.gs-slide-card.active {
  border: 1px solid #00ffff;
  background: #0d2b2d;
}

.gs-left-part {
  display: flex;
  align-items: center;
  flex: 1;
}

.gs-custom-checkbox {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid #336b6b;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: #092325;
  transition: all 0.2s;
  flex-shrink: 0;
}

.gs-custom-checkbox.checked {
  background-color: #00ffff;
  border-color: #00ffff;
}

.gs-custom-checkbox i {
  font-size: 12px;
}

.gs-thumb {
  width: 64px;
  height: 44px;
  background-color: #808080;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  margin-left: 16px;
  border-radius: 4px;
  flex-shrink: 0;
}

.gs-content {
  margin-left: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.gs-name {
  font-size: 13px;
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 4px;
}

.gs-meta {
  display: flex;
  align-items: center;
}

.gs-gray-text {
  color: #8c99a0;
  font-size: 9px;
  font-weight: bold;
  letter-spacing: 0.5px;
}

.gs-white-bold {
  color: #ffffff;
  font-weight: bold;
  font-size: 10px;
  margin-left: 6px;
  letter-spacing: 0.5px;
}

.gs-right-part {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  height: 38px;
  min-width: 70px;
}

.gs-view {
  font-size: 10px;
  color: #00ffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: bold;
}

.gs-view:hover {
  text-decoration: underline;
}

.gs-points {
  font-size: 10px;
  color: #00ffff;
  font-weight: bold;
}

/* TIMER */
.gs-timer {
  display: flex;
  border: 1px solid #163e41;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
}

.gs-timer-btn {
  flex: 1;
  text-align: center;
  padding: 14px;
  cursor: pointer;
  color: #8c99a0;
  font-size: 13px;
  font-weight: bold;
  background: #0f1c1f;
  transition: all 0.2s;
}

.gs-timer-btn:not(:last-child) {
  border-right: 1px solid #163e41;
}

.gs-timer-btn:hover:not(.active) {
  background: #15292b;
}

.gs-timer-btn.active {
  background-color: #00ffff;
  color: #0f1c1f;
  box-shadow: 0 0 15px rgba(0, 255, 255, 0.3);
  z-index: 1;
}

.gs-warning {
  font-size: 9px;
  text-align: center;
  color: #00ffff;
  font-weight: bold;
  letter-spacing: 0.5px;
}

/* FOOTER */
.gs-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 15px 24px;
  gap: 16px;
  background: #0d2525;
}

.gs-cancel {
  background: transparent;
  border: none;
  color: #c9d1d6;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  padding: 10px;
  transition: color 0.2s;
  letter-spacing: 0.5px;
}

.gs-cancel:hover {
  color: #ffffff;
}

.gs-start {
  background: #00ffff;
  color: #092325;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  font-size: 12px;
  display: flex;
  align-items: center;
  transition: background 0.2s, transform 0.1s;
  letter-spacing: 0.5px;
}

.gs-start:hover {
  background: #33ffff;
  transform: scale(1.02);
}

/* PREVIEW MODAL */
.gs-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100000;
}

.gs-preview-image {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.8);
  border: 1px solid #1a3c40;
}

.gs-preview-close {
  position: absolute;
  top: 24px;
  right: 24px;
  color: #8c99a0;
  font-size: 32px;
  cursor: pointer;
  transition: color 0.2s;
  z-index: 100001;
}

.gs-preview-close:hover {
  color: #fff;
}

.gs-cpa-close {
  position: absolute;
  top: 16px;
  right: 20px;
  color: #8c99a0;
  font-size: 20px;
  cursor: pointer;
  transition: color 0.2s;
  z-index: 10;
}

.gs-cpa-close:hover {
  color: #fff;
}

.gs-cpa-modal {
  background: #0d2729;
  border: 1px solid #00ffff;
  border-radius: 8px;
  padding: 32px;
  font-size: 16px;
  color: #fff;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.8);
  font-family: Arial, sans-serif;
  position: relative;
}

.gs-cpa-question {
  margin-bottom: 12px;
  line-height: 1.4;
  padding-right: 24px;
}

.gs-cpa-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gs-cpa-option {
  display: flex;
  align-items: flex-start;
  line-height: 1.4;
}

.gs-cpa-tick {
  color: #00e676; /* green check */
  margin-right: 8px;
  margin-top: 3px;
  font-size: 12px;
  width: 14px;
}

.gs-cpa-indent {
  margin-left: 22px;
}

.gs-start:disabled,
.gs-start.disabled {
  background-color: #ccc; /* light grey */
  color: #666; /* muted text */
  cursor: not-allowed; /* blocked cursor */
  opacity: 0.7; /* slight fade */
  pointer-events: none; /* prevent any interaction */
  box-shadow: none; /* remove shadow if any */
}

/* ── Mobile optimised view ───────────────────────────────────────────── */
@media (max-width: 600px) {
  /* Overlay — no padding, items start at top */
  .gs-overlay {
    padding: 0;
    align-items: flex-start;
  }

  /* Card fills the full screen */
  .gs-game-card {
    width: 100%;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
    border: none;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /* Header — tighter padding */
  .gs-header {
    padding: 12px 16px;
    flex-shrink: 0;
  }

  .gs-icon-box {
    font-size: 20px;
    margin-right: 10px;
  }

  .gs-title {
    font-size: 14px;
  }

  .gs-subtitle {
    font-size: 9px;
  }

  /* Section — leaner spacing */
  .gs-section {
    padding: 16px 14px 8px 14px;
  }

  /* Slides list — grow to fill remaining space */
  .gs-slides-list {
    max-height: none;
    flex: 1;
    overflow-y: auto;
  }

  /* Slide card — keep horizontal layout, tighten gaps */
  .gs-slide-card {
    padding: 10px 12px;
    margin-bottom: 8px;
  }

  /* Hide thumbnail on mobile to save space */
  .gs-thumb {
    display: none;
  }

  .gs-content {
    margin-left: 10px;
  }

  .gs-name {
    font-size: 12px;
  }

  .gs-gray-text {
    font-size: 8px;
  }

  .gs-white-bold {
    font-size: 9px;
  }

  /* Right-part — shrink min-width */
  .gs-right-part {
    min-width: 56px;
    height: auto;
    gap: 6px;
  }

  .gs-view {
    font-size: 9px;
  }

  .gs-points {
    font-size: 9px;
  }

  /* Timer buttons — full width, comfortable tap targets */
  .gs-timer-btn {
    padding: 12px 10px;
    font-size: 12px;
  }

  .gs-warning {
    font-size: 8px;
  }

  /* Footer — full-width buttons stacked horizontally */
  .gs-footer {
    padding: 12px 14px;
    flex-shrink: 0;
  }

  .gs-cancel {
    font-size: 11px;
  }

  .gs-start {
    padding: 10px 20px;
    font-size: 11px;
  }

  /* CPA preview modal — fits small screens */
  .gs-cpa-modal {
    width: 95%;
    padding: 20px 16px;
    font-size: 14px;
    max-height: 85vh;
  }

  .gs-cpa-question {
    padding-right: 28px;
  }

  /* Image preview — already responsive via max-width/max-height */
}
</style>
